import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const MAX_FEATURE_ITEMS = 5;
const MAX_FEATURE_CHARS = 1500;
const MAX_TAG_ITEMS = 13;
const MAX_TAG_CHARS = 250;

const uniqNormalize = (arr = []) =>
    [...new Set((arr || []).map((v) => String(v || "").trim()).filter(Boolean))];

const totalChars = (arr = []) => arr.reduce((sum, item) => sum + String(item || "").length, 0);

function FeatureTagsEditor({
    formData,
    setFormData,
    onSuggestFeatures,
    onSuggestTags,
    suggestLoading = false,
    tagSuggestions = [],
}) {
    const { t } = useTranslation();
    const [featureInput, setFeatureInput] = useState("");
    const [tagInput, setTagInput] = useState("");

    const productFeatures = useMemo(() => uniqNormalize(formData?.key_product_features || []), [formData?.key_product_features]);
    const tags = useMemo(() => uniqNormalize(formData?.search_terms || []), [formData?.search_terms]);

    const featureChars = totalChars(productFeatures);
    const tagChars = totalChars(tags);

    const pushItems = (targetKey, rawValue, maxItems, maxChars) => {
        const current = uniqNormalize(formData?.[targetKey] || []);
        const next = [...current];
        const chunks = String(rawValue || "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);

        for (const item of chunks) {
            if (next.length >= maxItems) break;
            if (next.includes(item)) continue;
            const candidateChars = totalChars(next) + item.length;
            if (candidateChars > maxChars) continue;
            next.push(item);
        }

        setFormData((prev) => ({ ...prev, [targetKey]: next }));
    };

    const removeAt = (targetKey, index) => {
        const current = uniqNormalize(formData?.[targetKey] || []);
        setFormData((prev) => ({ ...prev, [targetKey]: current.filter((_v, i) => i !== index) }));
    };

    return (
        <div className="d-flex flex-column mb-4 fv-row fv-plugins-icon-container">
            <div className="mb-3 d-flex align-items-center justify-content-between">
                <label className="d-flex align-items-center fs-6 fw-semibold mb-0">
                    <span>{t("Product Features")} ({productFeatures.length}/{MAX_FEATURE_ITEMS} - {featureChars}/{MAX_FEATURE_CHARS})</span>
                </label>
                <button type="button" className="btn btn-sm btn-light-primary" onClick={onSuggestFeatures} disabled={suggestLoading}>
                    <i className="fa fa-lightbulb-o"></i>
                </button>
            </div>
            <div className="input-group mb-2">
                <input
                    className="form-control"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            pushItems("key_product_features", featureInput, MAX_FEATURE_ITEMS, MAX_FEATURE_CHARS);
                            setFeatureInput("");
                        }
                    }}
                    placeholder={t("Enter Product Features, press Enter (can split by comma)")}
                />
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {productFeatures.map((item, idx) => (
                    <span key={`${item}-${idx}`} className="badge badge-light-primary d-flex align-items-center">
                        {item}
                        <button type="button" className="btn btn-sm btn-icon p-0 ms-2" onClick={() => removeAt("key_product_features", idx)}>
                            <i className="ki-outline ki-cross fs-7"></i>
                        </button>
                    </span>
                ))}
            </div>
            <div className="form-text mb-4">{t("Max 5 items, total up to 1500 characters.")}</div>

            <div className="mb-3 d-flex align-items-center justify-content-between">
                <label className="d-flex align-items-center fs-6 fw-semibold mb-0">
                    <span>{t("Tags")} ({tags.length}/{MAX_TAG_ITEMS} - {tagChars}/{MAX_TAG_CHARS})</span>
                </label>
                <button type="button" className="btn btn-sm btn-light-primary" onClick={onSuggestTags} disabled={suggestLoading}>
                    <i className="fa fa-lightbulb-o"></i>
                </button>
            </div>
            <div className="input-group mb-2">
                <input
                    className="form-control"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            pushItems("search_terms", tagInput, MAX_TAG_ITEMS, MAX_TAG_CHARS);
                            setTagInput("");
                        }
                    }}
                    placeholder={t("Enter Tags, press Enter (can split by comma)")}
                />
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {tags.map((item, idx) => (
                    <span key={`${item}-${idx}`} className="badge badge-light-info d-flex align-items-center">
                        {item}
                        <button type="button" className="btn btn-sm btn-icon p-0 ms-2" onClick={() => removeAt("search_terms", idx)}>
                            <i className="ki-outline ki-cross fs-7"></i>
                        </button>
                    </span>
                ))}
            </div>
            {!!tagSuggestions?.length && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                    {tagSuggestions.map((item, idx) => {
                        const normalized = String(item || "").trim();
                        const isSelected = tags.includes(normalized);
                        return (
                        <button
                            key={`${normalized}-${idx}`}
                            type="button"
                            className={`btn btn-sm ${isSelected ? "btn-light-success" : "btn-light-warning"}`}
                            onClick={() => pushItems("search_terms", normalized, MAX_TAG_ITEMS, MAX_TAG_CHARS)}
                            disabled={isSelected}
                        >
                            {isSelected ? "✓ " : "+ "}
                            {normalized}
                        </button>
                        );
                    })}
                </div>
            )}
            <div className="form-text">{t("Max 13 items, total up to 250 characters.")}</div>
        </div>
    );
}

export default FeatureTagsEditor;
